<script lang="ts">
  /**
   * The text input, on AppButton's tokens: rounded-control, fixed heights per size, brand-line
   * border. No separate focus ring to design — main.css's global :focus-visible already draws one —
   * this only needs the border itself to pick up the accent colour on focus, matching the treatment
   * AppButton's secondary variant uses on hover.
   */
  // $bindable, not a plain prop: most call sites in this codebase own their state in a store and pass
  // value + oninput one-way (see CatalogView's search box), which $bindable still supports exactly as
  // a plain prop would — but a couple of form-local fields (OrganizationDialog's create-org form) use
  // bind:value the ordinary Svelte way, and a plain prop would leave those with nowhere to bind to.
  // oninput/onchange are named explicitly, not left to ...rest: an untyped rest entry loses the DOM
  // event type, and every call site's inline handler ((event) => ...) would fail to compile under
  // noImplicitAny.
  let {
    value = $bindable(""),
    type = "text",
    size = "md",
    placeholder,
    disabled = false,
    oninput,
    onchange,
    ...rest
  }: {
    value?: string;
    type?: "text" | "search" | "email" | "password" | "number";
    size?: "lg" | "md" | "sm" | "xs";
    placeholder?: string;
    disabled?: boolean;
    oninput?: (event: Event & { currentTarget: HTMLInputElement }) => void;
    onchange?: (event: Event & { currentTarget: HTMLInputElement }) => void;
    [key: string]: unknown;
  } = $props();

  // Same heights as AppButton; padding is a touch tighter since an input has no icon competing for
  // the space a button's gap-2 reserves.
  const SIZES = {
    lg: "h-[var(--brand-control-h-lg)] px-4 text-base",
    md: "h-[var(--brand-control-h-md)] px-3.5 text-sm",
    sm: "h-[var(--brand-control-h-sm)] px-3 text-sm",
    xs: "h-[var(--brand-control-h-xs)] px-2.5 text-xs",
  } as const;
</script>

<input
  {type}
  bind:value
  {placeholder}
  {disabled}
  {oninput}
  {onchange}
  {...rest}
  class="w-full rounded-control border border-line bg-white text-ink placeholder:text-ink-muted
         transition-colors duration-200 ease-out
         hover:border-ink-muted focus:border-accent focus:outline-none
         disabled:opacity-50 disabled:cursor-not-allowed
         {SIZES[size]}"
/>
